import useAlias from '@alias';
import QueryKeys from '@src/constants/queryKeys';
import { ControlModes as StackControlModes } from '@src/types/Stacks';
import { notify } from '@store/actionCreators/notifications';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { getStackStrings, StackStrings } from '../../constants';
import {
  StackModalAction,
  StackModalActionType,
  StackModalState,
  UseStackModalReducerProps,
} from '../../types';
import { processControlModesForSave } from '../../utils/controlModeHelpers';
import { handleStackError } from '../../utils/errorHandlers';

const prepareStackData = (
  stackName: string,
  controlModes: StackControlModes[],
) => ({
  name: stackName,
  controlModes: processControlModesForSave(controlModes),
});

const getSuccessMessage = (
  isDefault: boolean,
  isUpdate: boolean,
  strings: StackStrings,
): string => {
  if (isDefault) {
    return isUpdate
      ? strings.SUCCESS_UPDATE_DEFAULT
      : strings.SUCCESS_CREATE_DEFAULT;
  }
  return isUpdate ? strings.SUCCESS_UPDATE : strings.SUCCESS_CREATE;
};

interface UseStackFormActionsProps {
  state: StackModalState;
  dispatch: React.Dispatch<StackModalAction>;
  props: UseStackModalReducerProps;
  validateForm: () => boolean;
  showAlert: (message: string) => void;
}

export const useStackFormActions = ({
  state,
  dispatch,
  props,
  validateForm,
  showAlert,
}: UseStackFormActionsProps) => {
  const { stackId, createStack, updateStack, onClose, isDefault } = props;

  const alias = useAlias();
  const STACK_STRINGS = useMemo(() => getStackStrings(alias), [alias]);
  const queryClient = useQueryClient();
  const reduxDispatch = useDispatch();

  const handleSave = useCallback(async () => {
    if (!validateForm()) {
      showAlert(alias('Please correct the errors before saving'));
      return;
    }

    dispatch({ type: StackModalActionType.SET_IS_SAVING, payload: true });

    try {
      const stackData = prepareStackData(state.stackName, state.controlModes);
      const isUpdate = Boolean(stackId);

      await (isUpdate ? updateStack : createStack).mutateAsync(stackData);
      notify(getSuccessMessage(isDefault ?? false, isUpdate, STACK_STRINGS))(
        reduxDispatch,
      );
      await queryClient.invalidateQueries({
        queryKey: [isDefault ? QueryKeys.defaultStack : QueryKeys.stacks],
      });
      onClose();
    } catch (error: unknown) {
      handleStackError(error, isDefault, stackId, reduxDispatch, queryClient);
    } finally {
      dispatch({ type: StackModalActionType.SET_IS_SAVING, payload: false });
    }
  }, [
    validateForm,
    showAlert,
    state.controlModes,
    state.stackName,
    stackId,
    updateStack,
    createStack,
    queryClient,
    onClose,
    isDefault,
    reduxDispatch,
    alias,
    STACK_STRINGS,
    dispatch,
  ]);

  const initializeForm = useCallback(
    (stackName: string, controlModes: StackControlModes[]) => {
      dispatch({
        type: StackModalActionType.INITIALIZE_FORM,
        payload: { stackName, controlModes },
      });
    },
    [dispatch],
  );

  const resetForm = useCallback(() => {
    dispatch({ type: StackModalActionType.RESET_FORM });
  }, [dispatch]);

  return {
    handleSave,
    initializeForm,
    resetForm,
  };
};
